import { useMutation, useQueryClient } from '@tanstack/react-query';
import { patientsApi } from '../api/patients.api';
import { patientsQueryKey } from './usePatients';
import type { AddPatientInput, AddPatientResult, Patient } from '../types/patient.types';

/**
 * Registers or attaches a patient to the account. Mirrors the mobile app:
 * the first patient on an account (GetPatient returned 204 / no patients) is
 * a *register* (`attachPatient: false`); anything after that is an attach.
 * On success we refresh the patient list so the new patient shows up
 * immediately in the switcher — and we WAIT for that refetch before the
 * caller's own onSuccess runs: the app shell keeps a no-patient account
 * locked on the add-patient screen until the list has a patient in it, so a
 * page that navigates away right after the add must not get bounced back.
 */
export function useAddPatient() {
  const queryClient = useQueryClient();

  return useMutation<AddPatientResult, Error, AddPatientInput>({
    mutationKey: ['patients', 'register'],
    mutationFn: async (input) => {
      const existing = await queryClient.ensureQueryData<Patient[]>({
        queryKey: patientsQueryKey,
        queryFn: patientsApi.getPatients,
      });
      return patientsApi.registerPatient({ attach: existing.length > 0, ...input });
    },
    onSuccess: (result) => {
      // Returning the promise makes TanStack await the refetch before the
      // mutate-level callbacks fire.
      if (result.success) {
        return queryClient.invalidateQueries({ queryKey: patientsQueryKey });
      }
      return undefined;
    },
  });
}
